<template>
  <div class="sd">
    <FiPageRail title="SOS Request Sender Details" noFav nowrapActions>
      <template #actions>
        <b-button v-if="!standalone" size="sm" variant="outline-secondary" :to="{ name: 'sos_list' }">
          <i class="fa fa-arrow-left"></i>&nbsp;Back
        </b-button>
      </template>
    </FiPageRail>

    <div v-if="loading" class="sd-loading">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <template v-else>
      <!-- title on the left, SOS lifecycle controls top-right (owner only) -->
      <div class="sd-topbar">
        <div class="sd-toprow">
          <div class="sd-title">{{ event.source_tenant_name || 'Company' }}<span class="sd-title-subject"> request for {{ requestSubject }}</span></div>
          <div v-if="!isReceived" class="sd-topactions">
            <b-button v-if="canEscalate && !showBroadcast && broadcastReachCount > 0" size="sm" variant="warning" :disabled="busy" @click="openBroadcast" title="Broadcast SOS" aria-label="Broadcast SOS"><i class="fa fa-bullhorn"></i><span class="sd-btn-label">&nbsp;Broadcast to {{ broadcastReachCount }} Yards</span></b-button>
            <b-button v-if="isOpen" size="sm" variant="outline-success" :disabled="busy" @click="closeSos" title="Mark resolved" aria-label="Mark resolved"><i class="fa fa-check"></i><span class="sd-btn-label">&nbsp;Mark resolved</span></b-button>
            <b-button v-if="isOpen" size="sm" variant="outline-secondary" :disabled="busy" @click="cancelSos" title="Cancel SOS" aria-label="Cancel SOS"><i class="fa fa-ban"></i><span class="sd-btn-label">&nbsp;Cancel SOS</span></b-button>
            <b-button size="sm" variant="outline-danger" :disabled="busy" @click="deleteSos" title="Delete" aria-label="Delete"><i class="fa fa-trash"></i><span class="sd-btn-label">&nbsp;Delete</span></b-button>
          </div>
        </div>

        <!-- review / update the request before broadcasting (mirrors the Send SOS page) -->
        <div v-if="showBroadcast" class="sd-bcast-edit">
          <div class="sd-bcast-edit-title">Review &amp; update before broadcasting</div>
          <div class="sd-bcast-grid">
            <label class="sd-bcast-field sd-bcast-field--wide"><span>Notes</span><b-form-textarea v-model="edit.request_notes" rows="2" max-rows="4" size="sm"/></label>
            <label class="sd-bcast-field"><span>Vehicle</span><b-form-input v-model="edit.vehicle_name" size="sm"/></label>
            <label class="sd-bcast-field"><span>Search radius (mi)</span><b-form-input v-model.number="edit.max_miles_away" type="number" min="1" max="500" size="sm"/></label>
            <!-- driver name/# is intentionally NOT here: it is never shared with partner companies
                 (only the operator contact is), so it must not appear as a broadcast field -->
            <label class="sd-bcast-field"><span>Operator contact</span><b-form-input v-model="edit.operator_contact_name" size="sm"/></label>
            <label class="sd-bcast-field"><span>Operator #</span><b-form-input v-model="edit.operator_contact_phone" size="sm"/></label>
          </div>
          <div class="sd-bcast-edit-actions">
            <b-button size="sm" variant="warning" :disabled="busy" @click="escalate"><i class="fa fa-bullhorn"></i>&nbsp;Broadcast now</b-button>
            <b-button size="sm" variant="outline-secondary" :disabled="busy" @click="showBroadcast = false">Cancel</b-button>
          </div>
        </div>
      </div>

      <!-- split view: request info + communication log on the left, map + yards on the right -->
      <div class="sd-split">
        <div class="sd-pane-map">
      <div class="sd-body">
        <!-- request info at the very top of the main column -->
        <div class="sd-details">
          <!-- on mobile the header drops "request for <vehicle>" to save width; surface it here instead -->
          <div class="sd-detail sd-detail--mobile-only"><span class="sd-detail-label">Request for</span><span class="sd-detail-value">{{ requestSubject }}</span></div>
          <div class="sd-detail"><span class="sd-detail-label">Requested</span><span class="sd-detail-value">{{ formatTime(event.processed_time) }}</span></div>
          <div class="sd-detail" v-if="driverName"><span class="sd-detail-label">Driver</span><span class="sd-detail-value">{{ driverName }}</span></div>
          <div class="sd-detail" v-if="driverPhone"><span class="sd-detail-label">{{ driverName ? 'Phone' : 'Driver' }}</span><span class="sd-detail-value"><a :href="'tel:' + driverPhone"><i class="fa fa-phone"></i>&nbsp;{{ driverPhone }}</a></span></div>
          <div class="sd-detail" v-if="event.operator_contact_name"><span class="sd-detail-label">Operator Contact</span><span class="sd-detail-value">{{ event.operator_contact_name }}</span></div>
          <div class="sd-detail" v-if="event.operator_contact_phone"><span class="sd-detail-label">Operator #</span><span class="sd-detail-value"><a :href="'tel:' + event.operator_contact_phone"><i class="fa fa-phone"></i>&nbsp;{{ event.operator_contact_phone }}</a></span></div>
          <div class="sd-detail" v-if="event.operator_contact_email"><span class="sd-detail-label">Operator Email</span><span class="sd-detail-value"><a :href="'mailto:' + event.operator_contact_email">{{ event.operator_contact_email }}</a></span></div>
          <div class="sd-detail sd-detail--wide"><span class="sd-detail-label">Location</span><span class="sd-detail-value" :title="sourceAddress">{{ sourceAddress || '—' }}</span></div>
          <div class="sd-detail sd-detail--wide" v-if="requestNotes"><span class="sd-detail-label">Notes</span><span class="sd-detail-value" :title="requestNotes">{{ requestNotes }}</span></div>
        </div>

        <!-- the SOS bus (collapsed by default); expand to see its recent track + booking + planned
             stops (reuses the nearby-vehicle live-context panel for the source vehicle) -->
        <div class="sd-group sd-group--sosbus">
          <button type="button" class="sd-group-title sd-group-title--toggle" @click="toggleGroup('sosbus')" :aria-expanded="!!expandedGroups.sosbus">
            <i class="fa" :class="expandedGroups.sosbus ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;SOS bus
          </button>
          <div v-for="veh in sourceLocs" :key="veh.name" v-show="expandedGroups.sosbus" class="sd-loc-wrap">
            <button type="button" class="sd-loc sd-loc--source" :class="{ 'sd-loc--selected': selectedSourceName === veh.name }" @click="select_source(veh)">
              {{ veh.name }}
              <span class="sd-veh-hint">{{ selectedSourceName === veh.name ? 'hide' : 'click for track &amp; trip' }}</span>
            </button>
            <sos-vehicle-context v-if="selectedSourceName === veh.name"
                                 :vehicle="veh" :standalone="standalone" :token="standaloneToken || ''"
                                 :request-id="($route.query.request_id) || ''" :tenant="(user && user.tenant) || ''"
                                 @breadcrumb="onBreadcrumb"/>
          </div>
          <!-- booking / itinerary for the SOS vehicle's run (shown with the SOS bus, only while that section is expanded) -->
          <div v-if="itineraryLegs.length" v-show="expandedGroups.sosbus" class="sd-itin sd-itin--sosbus">
            <div class="sd-itin-head">
              <span class="sd-itin-title"><i class="fa fa-route"></i>&nbsp;Booking / Itinerary</span>
              <span v-if="itineraryMeta" class="sd-itin-meta">{{ itineraryMeta }}</span>
              <b-form-checkbox v-if="isSourceEvent" v-model="shareItinerary" switch
                               :disabled="shareBusy || busy" class="sd-itin-share"
                               @change="onToggleShareItinerary">Share with partners</b-form-checkbox>
            </div>
            <div v-for="(leg, i) in itineraryLegs" :key="i" class="sd-itin-leg">
              <div v-if="leg.description" class="sd-itin-desc">{{ leg.description }}</div>
              <div v-for="(stop, j) in leg.stops" :key="j" class="sd-itin-stop">
                <span class="sd-itin-kind">{{ stop.kind }}</span>
                <span class="sd-itin-time">{{ formatTime(stop.time) }}</span>
                <span class="sd-itin-addr" :title="stop.address">{{ stop.address || '—' }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="sd-side">
          <div class="sd-group" v-if="routeLocs.length">
            <div class="sd-group-title">Planned route</div>
            <button type="button" v-for="veh in routeLocs" :key="veh.name"
                    class="sd-loc sd-loc--route" @click="focus_loc(veh.name)">{{ veh.name }} location</button>
          </div>

          <div class="sd-group" v-if="internalLocs.length">
            <button type="button" class="sd-group-title sd-group-title--toggle" @click="toggleGroup('internal')" :aria-expanded="!!expandedGroups.internal">
              <i class="fa" :class="expandedGroups.internal ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;your vehicles<span class="sd-group-count">{{ internalLocs.length }}</span>
            </button>
            <div v-for="veh in internalLocs" :key="veh.name" v-show="expandedGroups.internal" class="sd-loc-wrap">
              <button type="button" class="sd-loc sd-loc--internal sd-loc--compact" :class="{ 'sd-loc--selected': selectedVehicleName === veh.name }" @click="select_vehicle(veh)">
                <span class="sd-loc-num">V{{ vehicleNumbers[veh.name] }}</span>
                <span class="sd-veh-id">{{ veh.name }}<span v-if="veh.distance_from_sos != null" class="sd-veh-dist"> · {{ veh.distance_from_sos }} mi</span></span>
                <span class="sd-veh-hint">{{ selectedVehicleName === veh.name ? 'hide' : 'click for live info' }}</span>
              </button>
              <sos-vehicle-context v-if="selectedVehicleName === veh.name"
                                   :vehicle="veh" :standalone="standalone" :token="standaloneToken || ''"
                                   :request-id="($route.query.request_id) || ''" :tenant="(user && user.tenant) || ''"
                                   @breadcrumb="onBreadcrumb"/>
            </div>
          </div>

          <div class="sd-group" v-if="yourYards.length">
            <button type="button" class="sd-group-title sd-group-title--toggle" @click="toggleGroup('yours')" :aria-expanded="!!expandedGroups.yours">
              <i class="fa" :class="expandedGroups.yours ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;your yards<span class="sd-group-count">{{ yourYards.length }}</span>
            </button>
            <div v-for="veh in yourYards" :key="veh.name" v-show="expandedGroups.yours" class="sd-loc-wrap">
              <button type="button" class="sd-loc sd-loc--yard" :class="{ 'sd-loc--selected': selectedYardName === veh.name }" @click="select_yard(veh)">
                <span class="sd-loc-num">Y{{ yardNum(veh.name) }}</span>
                <span class="sd-loc-name">{{ veh.name }}<span v-if="veh.distance_from_sos != null" class="sd-loc-dist"> · {{ veh.distance_from_sos }} mi</span></span>
                <span v-if="veh.company && veh.company !== veh.name" class="sd-loc-company">{{ veh.company }}</span>
                <span v-if="veh.address" class="sd-loc-addr">{{ veh.address }}</span>
              </button>
              <div v-if="selectedYardName === veh.name" class="sd-loc-contact">
                <div v-if="veh.contact_name" class="sd-contact-line"><b>Contact:</b> {{ veh.contact_name }}</div>
                <a v-if="veh.phone" :href="'tel:' + veh.phone" class="sd-contact-phone"><i class="fa fa-phone"></i>&nbsp;{{ veh.phone }}</a>
                <div v-else class="sd-contact-none">No phone on file</div>
              </div>
            </div>
          </div>

          <div class="sd-group" v-if="otherYards.length">
            <button type="button" class="sd-group-title sd-group-title--toggle" @click="toggleGroup('yards')" :aria-expanded="!!expandedGroups.yards">
              <i class="fa" :class="expandedGroups.yards ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;other yards<span class="sd-group-count">{{ otherYards.length }}</span>
            </button>
            <!-- directory yards: the phone number sits on the row itself (no click needed to see it);
                 the row still centers the map on the yard -->
            <div v-for="veh in otherYards" :key="veh.name" v-show="expandedGroups.yards" class="sd-loc-wrap">
              <div class="sd-loc sd-loc--public sd-loc--phone-row" :class="{ 'sd-loc--selected': selectedYardName === veh.name }">
                <button type="button" class="sd-loc-main" @click="select_yard(veh)">
                  <span class="sd-loc-num">Y{{ yardNum(veh.name) }}</span>
                  <span class="sd-loc-name">{{ veh.name }}<span v-if="veh.distance_from_sos != null" class="sd-loc-dist"> · {{ veh.distance_from_sos }} mi</span></span>
                  <span v-if="veh.company && veh.company !== veh.name" class="sd-loc-company">{{ veh.company }}</span>
                  <span v-if="veh.address" class="sd-loc-addr">{{ veh.address }}</span>
                </button>
                <a v-if="veh.phone" :href="'tel:' + veh.phone" class="sd-loc-phone" :title="veh.contact_name ? 'Call ' + veh.contact_name : 'Call ' + veh.name" @click.stop><i class="fa fa-phone" aria-hidden="true"></i>&nbsp;{{ veh.phone }}</a>
                <span v-else class="sd-loc-phone sd-loc-phone--none">No phone</span>
              </div>
            </div>
          </div>
        </div>

        <div class="sd-mapwrap">
          <gmap-map ref="gmap"
                    :zoom="summary.zoom || 9"
                    :center="center"
                    :options="themed_map_options"
                    class="sd-map"
                    @click="editable ? onMapClick($event) : null">
            <gmap-circle v-if="pin && radiusMeters" :center="pin" :radius="radiusMeters" :options="mapCircleOptions"/>
            <gmap-marker
                v-for="(marker, idx) in markers"
                :key="idx"
                :position="editable && marker.type === 'source' && pin ? pin : marker.position"
                :title="marker.title"
                :icon="marker.icon"
                :label="marker.label"
                :clickable="true"
                :draggable="editable && marker.type === 'source'"
                @dragend="marker.type === 'source' ? onPinDrag($event) : null"
                @mouseover="openInfoWindowTemplate(idx)"
                @click="openInfoWindowTemplate(idx)"
                @mouseout="infoWindow.open = false"
            />
            <!-- opted-in partner yards in range (broadcast audience preview): pinned as amber
                 building icons numbered P1, P2… to match the audience list below the map -->
            <gmap-marker
                v-for="(y, i) in optedInYards"
                :key="'partner' + i"
                :position="{ lat: Number(y.lat), lng: Number(y.lon) }"
                :title="y.company || y.name"
                :icon="partnerYardIcon"
                :label="markerLabel('P' + (i + 1))"
                :clickable="true"
            />
            <!-- the selected nearby vehicle's last-hour GPS trail -->
            <gmap-polyline v-if="breadcrumbPath.length" :path="breadcrumbPath" :options="breadcrumbOptions"/>
            <gmap-info-window
                :options="{ pixelOffset: { width: 0, height: -38 } }"
                :position="infoWindow.position"
                :opened="infoWindow.open">
              <div v-html="infoWindow.template"></div>
            </gmap-info-window>
          </gmap-map>

          <!-- source dispatcher: drag the pin / slide the radius to change the broadcast audience -->
          <div v-if="editable" class="sd-area-edit">
            <div class="sd-area-label">Broadcast area — <strong>{{ editRadius }} mi</strong>
              <span class="sd-area-hint"> · drag the pin or tap the map<span v-if="savingLoc"> · updating&hellip;</span></span>
            </div>
            <input type="range" min="5" max="150" step="5" v-model.number="editRadius" class="sd-area-slider" aria-label="Broadcast search radius in miles"/>
            <div class="sd-area-audience">
              <span v-if="broadcastReachCount">
                <i class="fa fa-check-circle"></i>&nbsp;Broadcast reaches <strong>{{ broadcastReachCount }}</strong> partner{{ broadcastReachCount === 1 ? '' : 's' }}
              </span>
              <span v-else class="sd-area-none"><i class="fa fa-info-circle"></i>&nbsp;No opted-in partners within {{ editRadius }} mi — widen the radius or move the pin</span>
            </div>
            <!-- opted-in partners the broadcast would reach: named YARDS in range (a yard is public
                 info) plus an anonymous count of partners with only a VEHICLE in range. Collapsed to
                 the header by default; expand to see who. Clicking a partner centers its yard on the
                 map and shows its address / contact. -->
            <div v-if="optedInYards.length || vehicleOnlyCount" class="sd-area-list">
              <button type="button" class="sd-area-sub sd-area-sub--toggle" @click="broadcastListOpen = !broadcastListOpen" :aria-expanded="broadcastListOpen">
                <i class="fa" :class="broadcastListOpen ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;Partner yards or vehicles in range
              </button>
              <template v-if="broadcastListOpen">
                <div v-for="(y, i) in optedInYards" :key="'aud' + i" class="sd-area-wrap">
                  <button type="button" class="sd-area-row sd-area-row--btn" :class="{ 'sd-area-row--sel': selectedPartnerIdx === i }" @click="select_partner(y, i)">
                    <span class="sd-area-num">P{{ i + 1 }}</span>
                    <span class="sd-area-name">{{ y.name }}<span v-if="y.company && y.company !== y.name" class="sd-area-co"> · {{ y.company }}</span></span>
                    <span v-if="y.distance != null" class="sd-area-dist">{{ y.distance }} mi</span>
                  </button>
                  <div v-if="selectedPartnerIdx === i" class="sd-loc-contact">
                    <div v-if="y.address" class="sd-contact-line"><b>Address:</b> {{ y.address }}</div>
                    <a v-if="y.phone" :href="'tel:' + y.phone" class="sd-contact-phone"><i class="fa fa-phone"></i>&nbsp;{{ y.phone }}</a>
                    <div v-else class="sd-contact-none">No phone on file</div>
                  </div>
                </div>
                <!-- partners with only a VEHICLE in range: reached + counted, but never named -->
                <div v-if="vehicleOnlyCount" class="sd-area-veh">
                  <i class="fa fa-bus"></i>&nbsp;<strong>{{ vehicleOnlyCount }}</strong><span v-if="optedInYards.length"> more</span> compan{{ vehicleOnlyCount === 1 ? 'y has a vehicle' : 'ies have vehicles' }} in range
                </div>
              </template>
            </div>
          </div>
        </div>

      </div>
        </div><!-- /.sd-pane-map -->

        <div class="sd-pane-log">
      <!-- communication log (narrow right column): everything sent & received for this SOS, in order (signed-in staff only) -->
      <div class="sd-comlog" v-if="communicationLog.length">
        <div class="sd-comlog-title"><i class="fa fa-history"></i>&nbsp;Communication log</div>
        <div v-for="(c, i) in communicationLog" :key="i" class="sd-com" :class="'sd-com--' + c.type">
          <div class="sd-com-head">
            <span class="sd-com-badge">{{ c.title }}</span>
            <span v-if="c.time" class="sd-com-time">{{ formatTime(c.time) }}</span>
          </div>
          <div v-if="c.who" class="sd-com-who">{{ comWhoPrefix(c.type) }}{{ c.who }}</div>
          <div v-if="c.body" class="sd-com-body" v-html="linkify(c.body)"></div>
          <template v-if="c.body2">
            <div class="sd-com-sublabel">{{ c.body2label }}</div>
            <div class="sd-com-body" v-html="linkify(c.body2)"></div>
          </template>
          <!-- the full set of fields the dispatcher sent to partners with the broadcast -->
          <div v-if="c.fields && c.fields.length" class="sd-com-fields">
            <div v-for="(f, fi) in c.fields" :key="fi" class="sd-com-field">
              <span class="sd-com-field-k">{{ f.k }}</span>
              <span class="sd-com-field-v">{{ f.v }}</span>
            </div>
          </div>

          <!-- dispatch row keeps the not-notified warning; the lifecycle controls live top-right -->
          <template v-if="c.type === 'dispatch' && !isReceived">
            <div class="sd-com-warn" v-if="event.notification_failed">
              <i class="fa fa-exclamation-triangle"></i>&nbsp;No email/SMS recipient was configured — no alert was sent automatically.
            </div>
          </template>
        </div>

        <!-- add a comment to the shared driver / dispatch thread (interleaves above, in time order) -->
        <div v-if="actionToken" class="sd-com-add">
          <b-form-textarea v-model="commentText" rows="2" max-rows="4" size="sm" maxlength="500"
                           placeholder="Add a comment to the driver / dispatch thread…"/>
          <b-button size="sm" variant="primary" class="sd-com-add-send" :disabled="commentBusy || !commentText.trim()" @click="addComment">
            <b-spinner v-if="commentBusy" small></b-spinner>
            <template v-else><i class="fa fa-paper-plane"></i>&nbsp;Add comment</template>
          </b-button>
        </div>
      </div>

      <!-- once broadcast, send a free-text update to that same group (e.g. "found a bus, stand down") -->
      <div v-if="canRebroadcast" class="sd-rebcast sd-rebcast--panel">
        <label class="sd-rebcast-label">Send an update to the broadcast group</label>
        <b-form-textarea v-model="rebroadcastText" rows="2" max-rows="4" size="sm" maxlength="500"
                         placeholder="e.g. Nevermind, we found a bus — stand down. Thanks!"/>
        <b-button size="sm" variant="outline-warning" class="sd-rebcast-send" :disabled="busy || !rebroadcastText.trim()" @click="sendRebroadcast">
          <i class="fa fa-bullhorn"></i>&nbsp;Send update
        </b-button>
      </div>
        </div><!-- /.sd-pane-log -->
      </div><!-- /.sd-split -->
    </template>
  </div>
</template>
<script>
import {get_sos_details_authed, get_sos_detail_by_token, broadcast_sos_by_token, rebroadcast_sos_by_token, update_sos_by_token, get_sos_settings, close_sos_by_token, cancel_sos_by_token, delete_sos_by_token, add_sos_comment_by_token, set_sos_itinerary_shared_by_token} from '../networking/apis'
import {mapState} from "vuex";
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'
import SosVehicleContext from './sos_vehicle_context.vue'

const MARKER_COLORS = {
  source: '#e0574b',
  begin: '#3aa393',
  arrival: '#e0916b',
  internal: '#5aa974',
  yard: '#5b9bd5',
}

// yard pins are colored by which list group they belong to, so a pin on the map reads
// as the same category as its row in the list (your yards vs the general directory)
const YARD_GROUP_COLORS = {
  yours: '#2f7fd6',   // your own yards (blue)
  yards: '#7a8794',   // every other nearby directory yard (slate)
}

// opted-in partner yards in the broadcast audience preview — amber so they read as
// "who this broadcast would reach", distinct from the directory yards on the map
const PARTNER_YARD_COLOR = '#d98a2b'

const METERS_PER_MILE = 1609.34

export default {
  name: 'sos_sender_detail',
  components: { SosVehicleContext },
  // the SENDER / source view of an SOS. Two data modes:
  //   authed:      signed-in staff, addressed by ?request_id (get_sos_details_authed)
  //   standalone:  login-free dispatcher, addressed by the per-event share token (a `token` prop
  //                or the /sos/<slug>/<token>/dispatcher route param). A `payload` prop lets the
  //                sos_dispatch wrapper hand us the already-fetched detail so we skip the flash.
  // Every action (broadcast / rebroadcast / close / cancel / delete / comment) is token-based:
  // the token is event.share_token when authed, or the route/prop token when standalone.
  props: {
    // pre-fetched detail payload ({ summary, results, event, ... }); when present we render it
    // directly (standalone) instead of fetching
    payload: { type: Object, default: null },
    // the per-event share token for standalone actions (from the wrapper); falls back to the route
    token: { type: String, default: '' },
  },
  data() {
    return {
      items: [],
      sos_locations: [],
      internal_vehicles: [],
      nearby_yards: [],
      registered_yards: [],
      event: {},
      summary: {},
      loading: true,
      center: null,
      markers: [],
      standalone: true,
      // once we've rendered the wrapper-provided payload once, later reloads refetch by token so
      // post-action state (broadcast/close/comment) is fresh
      seeded: false,
      canBroadcast: false,
      canBroadcastReach: false,
      busy: false,
      showBroadcast: false,
      rebroadcastText: '',
      commentText: '',
      commentBusy: false,
      shareItinerary: false,   // reflects event.share_itinerary; re-seeded on every load_data
      shareBusy: false,        // in-flight guard for the share-itinerary toggle
      edit: {},
      selectedYardName: null,
      // which matching-location groups (nearby vehicles / yards) are expanded; all collapsed by default
      // so the page opens as a list of counts the dispatcher can drill into
      expandedGroups: {},
      broadcastListOpen: false,   // the partner-audience list is collapsed by default
      selectedPartnerIdx: null,   // which broadcast partner's address/contact panel is open
      // the nearby vehicle whose live "can this bus help?" panel is expanded, and its last-hour trail
      selectedVehicleName: null,
      selectedSourceName: null,   // the SOS bus's live-context panel (track + booking) is open
      vehicleBreadcrumb: [],
      // interactive broadcast-area editing (source dispatcher): drag the pin / slide the radius to
      // change which opted-in partners the broadcast would reach
      pin: null,          // editable source pin { lat, lng }
      savedPin: null,     // last-persisted pin (to detect real edits)
      editRadius: 60,     // editable search radius (miles)
      savedRadius: 60,
      savingLoc: false,
      autoSaveTimer: null,
      optedInYards: [],   // opted-in partner YARDS in range at the current pin/radius (named - public)
      vehicleOnlyCount: 0, // # more opted-in companies with only a vehicle in range (counted, not named)
      // matches the fleet map basemap (vehicle_map.vue); midnight users get the dark variant
      mapStyle: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'landscape.natural.terrain', stylers: [{ color: '#E0EDCA' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#DDDDDD' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        ],
      },
      infoWindow: {
        position: {lat: 0, lng: 0},
        open: false,
        template: ''
      }
    }
  },
  async created() {
    this.load_data()
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    themed_map_options() {
      // follow the SAME signal the page's CSS tokens use — the data-fi-theme attribute on <html> —
      // so the map's light/dark can never diverge from the panels. (Reading site_theme too keeps
      // this computed reactive to an in-session theme toggle.) Only the dark "midnight" horizon
      // variant gets dark tiles; the light horizon ('new') and 'original' keep the light basemap.
      const storeTheme = (this.$store.state.profile || {}).site_theme
      const attrTheme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-fi-theme')) || storeTheme
      const midnight = attrTheme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
    sourceAddress() {
      const g = this.event && this.event.source_gps_info
      return (g && g.reverseGeo && g.reverseGeo.formattedLocation) || ''
    },
    driverName() {
      const d = this.event && this.event.source_driver_details
      if (!d) return ''
      if (typeof d === 'string') return d
      return d.DriverName || d.driver_name || d.name || ''
    },
    driverPhone() {
      const d = this.event && this.event.source_driver_details
      let p = ''
      if (d && typeof d === 'object') p = d.source_driver_phone || d.driver_phone || d.phone || ''
      // origination_number is the source company's callback — surfaced on received copies too, so a
      // relief vehicle can reach the stranded driver
      if (!p) p = (this.event && this.event.origination_number) || ''
      // only surface a real phone number, not a staff username used as the origin
      return /\d{3}/.test(p) ? p : ''
    },
    requestNotes() {
      const n = this.event && this.event.request_notes
      return (n && n !== 'None') ? n : ''
    },
    requestSubject() {
      // what the request is for: the vehicle name, or "pin" for a pin-drop SOS with no vehicle
      return (this.event && this.event.source_vehicle_name) || 'pin'
    },
    responses() {
      return (this.event && this.event.responses) || []
    },
    broadcastRecipients() {
      return (this.event && this.event.broadcast_recipients) || []
    },
    broadcastHistory() {
      // the full list of broadcasts + follow-up updates (backend synthesizes a single entry for
      // events broadcast before the history list existed)
      return (this.event && this.event.broadcasts) || []
    },
    broadcastSentFields() {
      // the full set of fields the dispatcher sent to partners with the broadcast (the event's
      // fields are fixed once broadcast), shown under the "Broadcast to partners" log entry
      const e = this.event || {}
      const out = []
      const veh = e.source_vehicle_name ? ((e.source_vehicle_supplied ? 'Bus ' : '') + e.source_vehicle_name) : null
      if (veh) out.push({ k: 'Vehicle', v: veh })
      if (e.source_vehicle_type) out.push({ k: 'Vehicle type', v: e.source_vehicle_type })
      // driver name/# is never shared with partners, so it is not listed among the broadcast fields
      if (e.operator_contact_name) out.push({ k: 'Operator contact', v: e.operator_contact_name })
      if (e.operator_contact_phone) out.push({ k: 'Operator #', v: e.operator_contact_phone })
      if (e.operator_contact_email) out.push({ k: 'Operator email', v: e.operator_contact_email })
      if (this.sourceAddress) out.push({ k: 'Location', v: this.sourceAddress })
      if (e.max_miles_away) out.push({ k: 'Search radius', v: e.max_miles_away + ' mi' })
      if (this.requestNotes) out.push({ k: 'Notes', v: this.requestNotes })
      return out
    },
    communicationLog() {
      // a chronological feed of every message on this SOS: what the driver sent, what went to
      // dispatch, each response back to the driver, and each broadcast to other companies
      const e = this.event || {}
      const log = []
      // a received (partner) copy only sees what was broadcast to them — never the sender's private
      // driver message or internal comms. The sms_msg/email_msg on this copy IS that broadcast.
      if (this.isReceived) {
        if (e.sms_msg || e.email_msg) {
          log.push({ type: 'broadcast', time: e.processed_time,
                     title: 'Broadcast received from ' + (e.source_tenant_name || 'another company'),
                     who: null, body: e.sms_msg || e.email_msg })
        }
        return log
      }
      if (e.inbound_message || (e.pin_mode && (e.request_notes || e.operator_contact_name))) {
        // a dispatcher who raised the SOS from the Send SOS page is the initiator (their operator
        // contact name is set); a driver who texted in / used the driver link is not
        const initiatedBy = e.operator_contact_name
        // pin-mode intake is the raw "SOS PIN <lat>,<lon>" string — never surface coordinates; show
        // the human notes instead (the address is in the request-info panel above)
        const body = e.pin_mode ? (e.request_notes || null) : e.inbound_message
        log.push({ type: 'received', time: e.processed_time,
                   title: initiatedBy ? ('Initiated by ' + initiatedBy) : 'Received from driver',
                   who: initiatedBy ? null : (this.driverPhone || e.origination_number || null),
                   body })
      }
      // the acknowledgement the platform texted back to the driver at intake (pin / "SOS ..." SMS)
      if (e.driver_reply && e.driver_reply.text) {
        log.push({ type: 'reply', time: e.driver_reply.time, title: 'Reply to driver',
                   who: this.driverPhone || e.origination_number || null, body: e.driver_reply.text })
      }
      if (e.sms_msg || e.email_msg) {
        const to = [e.sms_recipients, e.email_recipients].filter(Boolean).join('  ·  ')
        log.push({ type: 'dispatch', time: e.processed_time, title: 'Sent to dispatch', who: to || null,
                   body: e.sms_msg || e.email_msg,
                   body2: (e.email_msg && e.email_msg !== e.sms_msg) ? e.email_msg : null, body2label: 'Email' })
      } else if (!this.isReceived) {
        // source event where no email/SMS recipient was configured: still the anchor row that
        // hosts the dispatcher's response / escalate / update actions
        log.push({ type: 'dispatch', time: e.processed_time, title: 'Dispatch', who: null, body: null })
      }
      const responses = this.responses || []
      responses.forEach(r => {
        log.push({ type: 'response', time: r.time, title: 'Response to driver — ' + this.statusLabel(r.status),
                   who: [r.responder_tenant_name, r.responder_name].filter(Boolean).join(' · ') || null,
                   body: r.note || null })
      })
      const broadcasts = this.broadcastHistory || []
      broadcasts.forEach(b => {
        // named recipients are yard-reached partners; partners reached only via a vehicle in range
        // are never named (vehicle positions are receiver-private) - show them as an operator count
        const names = (b.recipients || []).map(r => r.tenant_name || r.tenant_id).filter(Boolean)
        const vo = Number(b.vehicle_only_count) || 0
        if (vo) names.push(`${vo} nearby operator${vo === 1 ? '' : 's'}`)
        const entry = { type: 'broadcast', time: b.time,
                        title: b.kind === 'update' ? 'Broadcast update' : 'Broadcast to partners',
                        who: names.join(', ') || null,
                        body: b.message || null }
        // the initial broadcast also lists the full set of fields the dispatcher sent to partners
        if (b.kind !== 'update') entry.fields = this.broadcastSentFields
        log.push(entry)
      })
      // driver/dispatch comments interleave with everything else, in time order
      const comments = (e.comments || [])
      comments.forEach(c => {
        const role = c.role === 'dispatcher' ? 'Dispatch' : c.role === 'driver' ? 'Driver' : c.role === 'partner' ? 'Partner' : ''
        log.push({ type: 'comment', time: c.time, title: 'Comment',
                   who: [role, c.author].filter(Boolean).join(' · ') || null,
                   body: c.text || null })
      })
      return log.slice().sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0))
    },
    isSourceEvent() {
      // this is the sender page, so standalone (token-addressed) mode is always the source — the
      // only guard is that it's not a fanned-out partner copy. Authed mode additionally verifies
      // the viewing tenant owns it.
      if (this.standalone) return !(this.event && this.event.source_request_id)
      // a parent (source) event has no source_request_id and is owned by the viewing tenant
      return !!(this.event && !this.event.source_request_id && this.user &&
                String(this.event.source_tenant_id) === String(this.user.tenant))
    },
    isReceived() {
      // a fanned-out partner copy (source_request_id present) is an SOS received from another company
      return !!(this.event && this.event.source_request_id)
    },
    canEscalate() {
      // only the originating company can promote its own still-open internal SOS to a broadcast,
      // and only when it would actually reach an opted-in partner with a yard in range (a partner's
      // live vehicle positions are receiver-private and never surface to the sender)
      return this.isSourceEvent && this.canBroadcast && this.canBroadcastReach &&
             !this.event.broadcast && (this.event.sos_status || 'open') === 'open'
    },
    canRebroadcast() {
      // once broadcast, the source dispatcher can send a free-text update to that same group while
      // the SOS is still open (e.g. "found a bus, stand down")
      return this.isSourceEvent && !!this.event.broadcast &&
             (this.event.sos_status || 'open') === 'open'
    },
    standaloneToken() {
      // the per-event share token for a login-free dispatcher: the `token` prop (from the wrapper),
      // the /sos/<slug>/<token>/dispatcher route param, or a ?token= query (the /sos_map link)
      return this.token || this.$route.params.token || this.$route.query.token || null
    },
    isAuthedMode() {
      // signed-in staff addressed by ?request_id, with no standalone token / payload in play
      return !this.payload && !this.standaloneToken &&
             !!(this.user && this.user.tenant && this.$route.query.request_id)
    },
    actionToken() {
      // token every write action is addressed by: event.share_token when authed, the standalone
      // token otherwise
      return this.standalone
        ? this.standaloneToken
        : ((this.event && this.event.share_token) || null)
    },
    commentAuthor() {
      // who this comment is from in the shared thread: the signed-in staffer + this (source)
      // company, e.g. "Ben O'Day from DEMO-TBN"
      const name = (this.user && (this.user.full_name || this.user.username)) || null
      const company = (this.event && this.event.source_tenant_name) || null
      if (name && company) return name + ' from ' + company
      return name || company || null
    },
    isOpen() {
      return !!(this.event && (this.event.sos_status || 'open') === 'open')
    },
    editable() {
      // the source dispatcher can tune the broadcast area (pin + radius) up until it's broadcast:
      // open, source, broadcasting enabled, and not already sent out
      return this.isSourceEvent && this.isOpen && this.canBroadcast && !this.event.broadcast
    },
    radiusMeters() {
      return this.editRadius ? this.editRadius * METERS_PER_MILE : null
    },
    locationDirty() {
      if (!this.pin || !this.savedPin) return false
      return this.editRadius !== this.savedRadius ||
             Math.abs(this.pin.lat - this.savedPin.lat) > 1e-6 ||
             Math.abs(this.pin.lng - this.savedPin.lng) > 1e-6
    },
    mapCircleOptions() {
      return { strokeColor: '#c0392b', strokeOpacity: 0.5, strokeWeight: 1,
               fillColor: '#c0392b', fillOpacity: 0.06, clickable: false }
    },
    sourceLocs() {
      return (this.items || []).filter(i => i.type === 'source')
    },
    routeLocs() {
      return (this.items || []).filter(i => i.type === 'begin' || i.type === 'arrival')
    },
    internalLocs() {
      return (this.items || []).filter(i => i.type === 'internal')
    },
    // your own yards — kept as their own section at the top of the yard list
    yourYards() {
      return this.nearby_yards || []
    },
    // every other nearby directory yard in one section: association-network, public, and Fleet Int
    // customer yards alike. Opted-in partners in range are highlighted separately in the broadcast
    // area, so they aren't called out here
    otherYards() {
      return this.registered_yards || []
    },
    // number the nearby vehicles (V1, V2…) in list order so each bus pin's label maps to its row
    vehicleNumbers() {
      const m = {}
      this.internalLocs.forEach((v, i) => { m[v.name] = i + 1 })
      return m
    },
    // amber building icon for the opted-in partner yards in the broadcast audience preview
    partnerYardIcon() {
      return this.yardSymbol(PARTNER_YARD_COLOR)
    },
    // number the yards (Y1, Y2…) in the SAME order the list renders them — your yards, then each
    // network group, then public — and record each yard's group so its pin can be colored to match
    yardCatalog() {
      const m = {}
      let n = 0
      for (const y of this.yourYards) { m[y.name] = { num: ++n, group: 'yours' } }
      for (const y of this.otherYards) { m[y.name] = { num: ++n, group: 'yards' } }
      return m
    },
    // total companies the broadcast would reach: the named yard companies + the anonymous vehicle-only ones
    broadcastReachCount() {
      return this.optedInYards.length + this.vehicleOnlyCount
    },
    // normalize the source booking itinerary into ordered legs, each with the present stops
    // (Depart / Arrive / Return). Defensive: tolerates an absent field or a non-list legs value.
    itineraryLegs() {
      const it = this.event && this.event.source_booking_itinerary
      const legs = it && Array.isArray(it.legs) ? it.legs : []
      return legs.map(l => {
        const stops = []
        if (l.begin_time || l.begin_address) stops.push({ kind: 'Depart', time: l.begin_time, address: l.begin_address })
        if (l.arrival_time || l.arrival_address) stops.push({ kind: 'Arrive', time: l.arrival_time, address: l.arrival_address })
        if (l.end_time || l.end_address) stops.push({ kind: 'Return', time: l.end_time, address: l.end_address })
        return { description: l.description || '', stops }
      }).filter(l => l.stops.length)
    },
    // booking label / number shown beside the itinerary title, when present
    itineraryMeta() {
      const it = this.event && this.event.source_booking_itinerary
      if (!it) return ''
      const parts = []
      if (it.label) parts.push(it.label)
      if (it.booking_id) parts.push('#' + it.booking_id)
      return parts.join(' · ')
    },
    breadcrumbPath() {
      return (this.vehicleBreadcrumb || [])
        .map(p => ({ lat: Number(p.latitude), lng: Number(p.longitude) }))
        .filter(p => !isNaN(p.lat) && !isNaN(p.lng))
    },
    breadcrumbOptions() {
      return { strokeColor: '#2f7d4f', strokeOpacity: 0.85, strokeWeight: 3, clickable: false, geodesic: true }
    },
  },
  methods: {
    formatTime(iso) {
      if (!iso) return '—'
      const dt = new Date(iso)
      if (isNaN(dt.getTime())) return iso
      return dt.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    focus_loc(selected_name) {
      const loc = (this.items || []).filter(i => i.name === selected_name)[0]
      if (loc != null) {
        this.center = { lat: Number(loc.latitude), lng: Number(loc.longitude) }
        if (this.$refs.gmap) {
          this.$refs.gmap.$mapPromise.then(map => { map.setZoom(12) })
        }
      }
    },
    // zoom/pan so every pin (source, vehicles, yards) is in view — the fixed zoom clipped
    // markers near the edge of the radius on small/mobile viewports
    fit_to_markers() {
      if (!this.$refs.gmap || !this.markers.length) return
      this.$refs.gmap.$mapPromise.then((map) => {
        const g = window.google && window.google.maps
        if (!g) return
        if (this.markers.length === 1) {
          map.setCenter(this.markers[0].position)
          map.setZoom(this.summary.zoom || 12)
          return
        }
        const bounds = new g.LatLngBounds()
        this.markers.forEach(m => bounds.extend(new g.LatLng(m.position.lat, m.position.lng)))
        map.fitBounds(bounds)
      }).catch(() => {})
    },
    fit_to_radius() {
      // when the search radius changes, zoom the map so the WHOLE circle stays visible (plus
      // every marker) instead of letting the circle spill past the map edge
      if (!this.$refs.gmap || !this.pin || !this.radiusMeters) return
      this.$refs.gmap.$mapPromise.then((map) => {
        const g = window.google && window.google.maps
        if (!g) return
        const latOff = this.radiusMeters / 111320
        const lngOff = this.radiusMeters / (111320 * Math.cos(this.pin.lat * Math.PI / 180))
        const bounds = new g.LatLngBounds()
        bounds.extend(new g.LatLng(this.pin.lat + latOff, this.pin.lng + lngOff))
        bounds.extend(new g.LatLng(this.pin.lat - latOff, this.pin.lng - lngOff))
        this.markers.forEach(m => bounds.extend(new g.LatLng(m.position.lat, m.position.lng)))
        map.fitBounds(bounds)
      }).catch(() => {})
    },
    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key])
    },
    select_yard(veh) {
      if (this.selectedYardName === veh.name) {
        this.selectedYardName = null
        return
      }
      this.selectedYardName = veh.name
      this.focus_loc(veh.name)
    },
    // a broadcast-audience partner isn't in `items` (it's its own opted_in_yards list with a P-pin),
    // so center the map directly on its yard coords and toggle its address/contact panel
    select_partner(y, i) {
      if (this.selectedPartnerIdx === i) {
        this.selectedPartnerIdx = null
        return
      }
      this.selectedPartnerIdx = i
      const lat = Number(y.lat), lng = Number(y.lon)
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        this.center = { lat, lng }
        if (this.$refs.gmap) {
          this.$refs.gmap.$mapPromise.then(map => { map.setZoom(12) }).catch(() => {})
        }
      }
    },
    select_vehicle(veh) {
      // toggle the live-status panel for a nearby vehicle; clear the map trail when collapsing
      if (this.selectedVehicleName === veh.name) {
        this.selectedVehicleName = null
        this.vehicleBreadcrumb = []
        return
      }
      this.selectedVehicleName = veh.name
      this.selectedSourceName = null   // only one live-context panel + map trail at a time
      this.vehicleBreadcrumb = []
      this.focus_loc(veh.name)
    },
    select_source(veh) {
      // the SOS bus itself: toggle its live-context panel (recent track + booking + planned stops),
      // reusing the nearby-vehicle context component. Clears any nearby-vehicle panel so only one
      // trail draws on the map at a time.
      if (this.selectedSourceName === veh.name) {
        this.selectedSourceName = null
        this.vehicleBreadcrumb = []
        return
      }
      this.selectedSourceName = veh.name
      this.selectedVehicleName = null
      this.vehicleBreadcrumb = []
      this.focus_loc(veh.name)
    },
    onBreadcrumb(points) {
      this.vehicleBreadcrumb = Array.isArray(points) ? points : []
    },
    openInfoWindowTemplate(index) {
      const loc = this.items[index]
      this.infoWindow.position = { lat: Number(loc.latitude), lng: Number(loc.longitude) }
      this.infoWindow.template = loc.marker_html
      this.infoWindow.open = true
    },
    async load_data() {
      this.loading = true
      try {
        this.summary = {}
        this.items = []
        this.markers = []

        // signed-in staff get the full event (messages, recipients, responses) from the authed,
        // tenant-scoped endpoint. A login-free dispatcher gets the same rich payload from the
        // token endpoint (event public-stripped). When the sos_dispatch wrapper handed us the
        // already-fetched payload we seed from it, but still refresh from the token so that
        // post-action reloads reflect the change.
        let data = {}
        if (this.isAuthedMode) {
          this.standalone = false
          const {value: { data: d = {} } } = await get_sos_details_authed(this.user.tenant, this.$route.query.request_id)
          data = d
        } else {
          this.standalone = true
          if (this.payload && !this.seeded) {
            // first render from the wrapper-provided payload — no fetch
            this.seeded = true
            data = this.payload
          } else if (this.standaloneToken) {
            const {value: { data: d = {} } } = await get_sos_detail_by_token(this.standaloneToken)
            data = d
          } else if (this.payload) {
            data = this.payload
          }
        }

        this.summary = data.summary || {}
        this.items = data.results || []
        this.event = data.event || {}
        this.shareItinerary = !!this.event.share_itinerary   // re-sync the switch on every reload
        this.canBroadcastReach = !!data.can_broadcast_reach
        this.sos_locations = data.sos_locations || []
        this.internal_vehicles = data.internal_vehicles || []
        this.nearby_yards = data.nearby_yards || []
        this.registered_yards = data.registered_yards || []

        this.rebuildMarkers(true)

        // seed the editable pin + radius from the loaded event (don't clobber an in-progress drag)
        if (!this.locationDirty) {
          const src = (this.items || []).find(i => i.type === 'source')
          this.pin = src ? { lat: Number(src.latitude), lng: Number(src.longitude) } : null
          this.editRadius = Number(this.event.max_miles_away) || 60
          this.savedPin = this.pin ? { ...this.pin } : null
          this.savedRadius = this.editRadius
        }

        // gate the Escalate-to-Broadcast button. signed-in staff read their tenant's SOS settings;
        // a login-free dispatcher can't (no auth), so rely on the server-computed reach flag.
        if (!this.standalone && this.user && this.user.tenant) {
          try {
            const {value: { data: s = {} } } = await get_sos_settings(this.user.tenant)
            this.canBroadcast = !!(s.enabled && s.send_to_other_companies)
          } catch (e) {
            this.canBroadcast = false
          }
        } else {
          this.canBroadcast = true
        }
        // populate the live "broadcast reaches" audience for the current pin/radius (source + open)
        if (this.editable) this.refreshAudience()
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
      this.$nextTick(this.fit_to_markers)
    },
    rebuildMarkers(recenter = false) {
      // build the map markers from this.items (source pin, own vehicles, own/directory yards).
      // recenter only on a full load - a lightweight refresh must not move the map.
      this.markers = []
      for (const loc of this.items) {
        const position = { lat: Number(loc.latitude), lng: Number(loc.longitude) }
        if (recenter && loc.type === 'source') this.center = position
        let color = MARKER_COLORS[loc.type] || MARKER_COLORS.yard
        let label = null
        if (loc.type === 'internal') {
          // vehicles: number the bus pin (V1, V2…) to match its row in the nearby-vehicles list
          const n = this.vehicleNumbers[loc.name]
          if (n) label = this.markerLabel('V' + n)
        } else if (loc.type === 'yard') {
          // yards: color the building pin by its list group and number it (Y1, Y2…) to match its row
          const meta = this.yardCatalog[loc.name]
          if (meta) { color = YARD_GROUP_COLORS[meta.group] || color; label = this.markerLabel('Y' + meta.num) }
        }
        this.markers.push({ position, title: loc.name, icon: this.markerIcon(loc.type, color), type: loc.type, label })
      }
    },
    async refreshOwnMatches() {
      // after a pin/radius change the server re-pulls this tenant's own vehicles/yards; re-fetch the
      // detail so the newly in-range fleet/yards render. No spinner and no map move - the radius
      // watcher already fit the view; only the own-match lists + their markers update.
      try {
        let data = {}
        if (!this.standalone && this.user && this.user.tenant && this.$route.query.request_id) {
          const { value: { data: d = {} } } = await get_sos_details_authed(this.user.tenant, this.$route.query.request_id)
          data = d
        } else if (this.standaloneToken) {
          const { value: { data: d = {} } } = await get_sos_detail_by_token(this.standaloneToken)
          data = d
        } else {
          return
        }
        this.items = data.results || []
        this.internal_vehicles = data.internal_vehicles || []
        this.nearby_yards = data.nearby_yards || []
        this.registered_yards = data.registered_yards || []
        this.rebuildMarkers(false)
      } catch (e) { /* best-effort own-match refresh */ }
    },
    onPinDrag(e) {
      this.pin = { lat: e.latLng.lat(), lng: e.latLng.lng() }
    },
    onMapClick(e) {
      if (!this.editable) return
      this.pin = { lat: e.latLng.lat(), lng: e.latLng.lng() }
    },
    queueAutoSave() {
      // persist the new pin/radius shortly after the dispatcher stops adjusting, then refresh audience
      if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
      this.autoSaveTimer = setTimeout(this.autoSaveLocation, 700)
    },
    async autoSaveLocation() {
      if (!this.pin || !this.locationDirty || this.savingLoc || !this.actionToken) return
      this.savingLoc = true
      const pin = { ...this.pin }
      const radius = this.editRadius
      try {
        const {value: { data = {} }} = await update_sos_by_token(this.actionToken,
          { lat: pin.lat.toFixed(6), lon: pin.lng.toFixed(6), max_miles_away: radius })
        this.savedPin = pin
        this.savedRadius = radius
        // keep the local event's radius current so the request facts and the Broadcast form read
        // the newly-saved value instead of the radius the SOS was originally raised with
        if (this.event) this.$set(this.event, 'max_miles_away', radius)
        this.applyAudience(data)
        // the server re-pulled our own fleet/yards for the new pin/radius; render them
        await this.refreshOwnMatches()
      } catch (e) {
        this.$emit('showAlert', 'could not update the broadcast area', 'danger', 4000)
      }
      this.savingLoc = false
    },
    async refreshAudience() {
      // read who the broadcast reaches at the CURRENT radius (radius-only, so the stored pin isn't
      // re-geocoded); the update endpoint returns the opted-in partners in range
      if (!this.actionToken || this.savingLoc) return
      try {
        const {value: { data = {} }} = await update_sos_by_token(this.actionToken, { max_miles_away: this.editRadius })
        this.applyAudience(data)
      } catch (e) { /* best-effort audience preview */ }
    },
    applyAudience(data) {
      if (data && Array.isArray(data.opted_in_yards)) {
        this.optedInYards = data.opted_in_yards
        this.selectedPartnerIdx = null   // the list changed; drop any open partner panel (indexes shifted)
        // companies with only a vehicle in range: reached + counted, but never named (receiver-private)
        this.vehicleOnlyCount = Number(data.vehicle_only_partner_count) || 0
        this.canBroadcastReach = this.optedInYards.length > 0 || this.vehicleOnlyCount > 0
      }
    },
    openBroadcast() {
      // prefill the review form from the current event. Driver name/# is intentionally omitted -
      // it is never shared with partners, so it is not a broadcast field.
      const e = this.event || {}
      this.edit = {
        request_notes: this.requestNotes,
        vehicle_name: e.source_vehicle_name || '',
        operator_contact_name: e.operator_contact_name || '',
        operator_contact_phone: e.operator_contact_phone || '',
        // use the radius the dispatcher currently has selected on the slider, not the value the SOS
        // was originally raised with (which would otherwise snap the broadcast back to the old radius)
        max_miles_away: this.editRadius || (e.max_miles_away != null ? Number(e.max_miles_away) : 60),
      }
      this.showBroadcast = true
    },
    async escalate() {
      if (!window.confirm('Broadcast this SOS to nearby partner companies that have opted in?')) return
      this.busy = true
      try {
        const {value: { data = {} } } = await broadcast_sos_by_token(this.actionToken, this.edit)
        this.$emit('showAlert', 'SOS broadcast to ' + (data.queued || 0) + ' partner companies', 'success', 4000)
        this.showBroadcast = false
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'could not broadcast SOS', 'danger', 4000)
      }
      this.busy = false
    },
    async sendRebroadcast() {
      const message = (this.rebroadcastText || '').trim()
      if (!message) return
      if (!window.confirm('Send this update to the companies this SOS was broadcast to?')) return
      this.busy = true
      try {
        const {value: { data = {} } } = await rebroadcast_sos_by_token(this.actionToken, message)
        this.$emit('showAlert', 'Update sent to ' + (data.notified || 0) + ' companies', 'success', 4000)
        this.rebroadcastText = ''
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'could not send update', 'danger', 4000)
      }
      this.busy = false
    },
    async closeSos() {
      if (!window.confirm('Mark this SOS as resolved?')) return
      this.busy = true
      try {
        await close_sos_by_token(this.actionToken)
        this.$emit('showAlert', 'SOS marked resolved', 'success', 2500)
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'could not close SOS', 'danger', 4000)
      }
      this.busy = false
    },
    async cancelSos() {
      if (!window.confirm('Cancel this SOS? The driver will see it as cancelled.')) return
      this.busy = true
      try {
        await cancel_sos_by_token(this.actionToken)
        this.$emit('showAlert', 'SOS cancelled', 'success', 2500)
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'could not cancel SOS', 'danger', 4000)
      }
      this.busy = false
    },
    async deleteSos() {
      if (!window.confirm('Delete this SOS permanently? This cannot be undone.')) return
      this.busy = true
      try {
        await delete_sos_by_token(this.actionToken)
        this.$emit('showAlert', 'SOS deleted', 'success', 2500)
        // the event is gone; leave the detail view (only the authed staff view has a list to return to)
        if (!this.standalone) this.$router.push('/sos_list')
      } catch (e) {
        this.$emit('showAlert', 'could not delete SOS', 'danger', 4000)
        this.busy = false
      }
    },
    async addComment() {
      const text = (this.commentText || '').trim()
      if (!text || this.commentBusy) return
      this.commentBusy = true
      try {
        await add_sos_comment_by_token(this.actionToken,
          { text, role: 'dispatcher', author: this.commentAuthor })
        this.commentText = ''
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'could not add comment', 'danger', 4000)
      }
      this.commentBusy = false
    },
    // share / un-share the booking itinerary with the partners the SOS reached. The switch is the
    // source of truth for the optimistic UI; on failure we revert it to match the server.
    async onToggleShareItinerary(val) {
      if (this.shareBusy) return
      this.shareBusy = true
      try {
        await set_sos_itinerary_shared_by_token(this.actionToken, val)
        this.event.share_itinerary = val
        this.$emit('showAlert', val ? 'Itinerary shared with partners' : 'Itinerary no longer shared', 'success', 2500)
      } catch (e) {
        this.shareItinerary = !val   // revert the switch to the last-known server state
        this.$emit('showAlert', 'could not update itinerary sharing', 'danger', 4000)
      }
      this.shareBusy = false
    },
    statusLabel(s) {
      return ({ acknowledged: 'Acknowledged', en_route: 'En route', arrived: 'Arrived',
                declined: "Can't help", unavailable: 'Unavailable', resolved: 'Resolved' })[s] || s
    },
    comWhoPrefix(type) {
      return type === 'received' ? 'From ' : type === 'response' ? 'By ' : type === 'comment' ? '' : 'To '
    },
    linkify(text) {
      // render a log body as escaped text with any http(s) URL turned into a clickable link plus a
      // small open-in-new-tab icon to its right. Everything is HTML-escaped first, and only the
      // anchors we build are injected, so user-supplied text can't inject markup.
      const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
      const urlRe = /(https?:\/\/[^\s]+)/g
      let out = ''
      let last = 0
      let m
      while ((m = urlRe.exec(text)) !== null) {
        out += esc(text.slice(last, m.index))
        let url = m[1]
        let tail = ''
        const trailing = url.match(/[.,;:!?)\]]+$/)   // don't swallow sentence punctuation after the URL
        if (trailing) { tail = trailing[0]; url = url.slice(0, url.length - tail.length) }
        const safe = esc(url)
        out += '<a href="' + safe + '" target="_blank" rel="noopener noreferrer" class="sd-com-link">' + safe + '</a>'
        out += '<a href="' + safe + '" target="_blank" rel="noopener noreferrer" class="sd-com-open" title="Open in a new tab" aria-label="Open in a new tab"><i class="fa fa-external-link"></i></a>'
        out += esc(tail)
        last = m.index + m[1].length
      }
      out += esc(text.slice(last))
      return out
    },
    pinSymbol(color) {
      // square markers, consistent with the SOS overlay map
      return {
        path: 'M -3,-3 L 3,-3 L 3,3 L -3,3 Z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 0.6,
        scale: 3.4,
      }
    },
    busSymbol(color, scale = 1.3) {
      // a small bus silhouette (body + two wheels) so nearby vehicles read clearly as buses
      return {
        path: 'M -10,-5 L 10,-5 L 10,3 L 7,3 L 7,5 L 4,5 L 4,3 L -4,3 L -4,5 L -7,5 L -7,3 L -10,3 Z',
        fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 0.8, scale,
      }
    },
    yardSymbol(color) {
      // a small building/depot silhouette so yards read clearly as yards
      return {
        path: 'M -8,0 L 0,-7 L 8,0 L 5.5,0 L 5.5,6 L -5.5,6 L -5.5,0 Z',
        fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 0.8, scale: 1.4,
      }
    },
    markerIcon(type, color) {
      if (type === 'source') return this.busSymbol(color, 1.4)  // the SOS bus itself -> a red bus, slightly larger than responders
      if (type === 'internal') return this.busSymbol(color)    // nearby responder vehicles -> bus
      if (type === 'yard') return this.yardSymbol(color)       // yards -> building
      return this.pinSymbol(color)                             // itinerary stops -> square
    },
    // white number centered on the pin, matching the badge on the matching list row
    markerLabel(text) {
      return { text: String(text), color: '#ffffff', fontSize: '10px', fontWeight: '700' }
    },
    yardNum(name) {
      const meta = this.yardCatalog[name]
      return meta ? meta.num : ''
    },
  },
  beforeDestroy() {
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    },
    editRadius() { if (this.editable) { this.queueAutoSave(); this.fit_to_radius() } },
    pin: {
      deep: true,
      handler() { if (this.editable) this.queueAutoSave() },
    },
  }
}
</script>
<style scoped>
.sd {
  padding: 16px;
  max-width: 100%;
  overflow-x: hidden;   /* never let the page scroll sideways on narrow screens */
}
.sd-details {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 20px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 10px;
  padding: 8px 14px;
  margin-bottom: 12px;
}
.sd-detail {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
}
/* the "Request for <vehicle>" row is only surfaced on mobile, where the header drops it */
.sd-detail--mobile-only {
  display: none;
}
.sd-detail--wide .sd-detail-value {
  /* SOS notes always show in full: wrap across lines rather than truncating to one */
  max-width: 100%;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
}
.sd-detail-label {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
  white-space: nowrap;
}
.sd-detail-value {
  font-size: 15px;
  color: var(--fi-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 340px;
}
.sd-detail-value a {
  color: var(--fi-link);
  text-decoration: none;
}
.sd-detail-value a:hover {
  text-decoration: underline;
}
.sd-itin {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.sd-itin-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.sd-itin-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-itin-meta {
  font-size: 13px;
  color: var(--fi-text-muted);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-itin-share { margin-left: auto; }
.sd-itin-leg {
  padding-top: 6px;
  margin-top: 6px;
  border-top: 1px solid var(--fi-line);
}
.sd-itin-leg:first-of-type {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}
.sd-itin-desc {
  font-size: 13px;
  font-weight: 600;
  color: var(--fi-text);
  margin-bottom: 2px;
}
.sd-itin-stop {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
}
.sd-itin-kind {
  flex: 0 0 54px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-itin-time {
  flex: 0 0 auto;
  color: var(--fi-text);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.sd-itin-addr {
  color: var(--fi-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.sd-info-list {
  list-style: none;
  padding: 0;
  margin: 8px 0;
}
.sd-info-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.sd-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.sd-dot--source { background: #e0574b; }
.sd-dot--internal { background: #5aa974; }
.sd-dot--yard { background: #2f7fd6; }
.sd-dot--network { background: #4b45b8; }
.sd-dot--public { background: #7a8794; }
.sd-info-note {
  color: var(--fi-text-faint);
  font-size: 14px;
}
.sd-loading {
  text-align: center;
  padding: 40px;
}
.sd-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.sd-pill {
  font-size: 13px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 999px;
}
.sd-pill--sent { background: color-mix(in srgb, #2b5aa6 12%, var(--fi-surface)); color: color-mix(in srgb, #2b5aa6 55%, var(--fi-text)); }
.sd-pill--received { background: color-mix(in srgb, #5a3fa6 12%, var(--fi-surface)); color: color-mix(in srgb, #5a3fa6 55%, var(--fi-text)); }
.sd-pill--source { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-pill--internal { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-pill--yard { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-pill--broadcast { background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-pill--warn { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }

.sd-responses {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.sd-resp-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.sd-resp-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-resp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sd-resp-escalate { margin-left: auto; }
.sd-resp-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sd-resp-item {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 13px;
  padding: 4px 0;
  border-top: 1px solid var(--fi-line);
}
.sd-resp-status {
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--fi-surface-sunken);
  color: var(--fi-text-muted);
}
.sd-resp-status--en_route, .sd-resp-status--acknowledged, .sd-resp-status--arrived { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-resp-status--declined, .sd-resp-status--unavailable { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-resp-who { color: var(--fi-text); font-weight: 600; }
.sd-resp-note { color: var(--fi-text-muted); }
.sd-resp-time { margin-left: auto; color: var(--fi-text-faint); font-size: 12px; }
.sd-resp-empty { font-size: 13px; color: var(--fi-text-faint); }

.sd-bcast-edit {
  margin-top: 10px;
  padding: 10px 12px;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--fi-warning-accent) 35%, var(--fi-line));
  border-radius: 10px;
}
.sd-bcast-edit-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
  margin-bottom: 8px;
}
.sd-bcast-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}
.sd-bcast-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 180px;
  min-width: 0;
  margin-bottom: 0;
}
.sd-bcast-field--wide { flex-basis: 100%; }
.sd-bcast-field > span {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-bcast-edit-actions { display: flex; gap: 8px; margin-top: 10px; }
.sd-bcast-history {
  margin-top: 10px;
  padding: 8px 12px;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sd-bcast-history-title { font-weight: 700; color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-bcast-entry {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 6px;
  border-top: 1px solid color-mix(in srgb, var(--fi-warning-accent) 35%, var(--fi-line));
}
.sd-bcast-entry:first-of-type { border-top: none; padding-top: 0; }
.sd-bcast-entry-head { display: flex; align-items: center; gap: 8px; }
.sd-bcast-kind {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border-radius: 999px;
  padding: 1px 9px;
}
.sd-bcast-kind--send { background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-bcast-kind--update { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-bcast-when { color: var(--fi-text-faint); font-size: 12px; }
.sd-bcast-msg { color: var(--fi-text); }
.sd-bcast-recips { display: flex; flex-wrap: wrap; gap: 6px; }
.sd-bcast-recip {
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text));
  border-radius: 999px;
  padding: 2px 10px;
  font-weight: 600;
}
.sd-bcast-none { color: var(--fi-text-faint); }
.sd-rebcast {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sd-rebcast-label { font-size: 13px; font-weight: 600; color: var(--fi-text-muted); margin: 0; }
.sd-rebcast-send { align-self: flex-start; font-weight: 600; }
.sd-resp-hint {
  font-size: 13px;
  color: var(--fi-text-faint);
  background: var(--fi-surface-sunken);
  border-radius: 8px;
  padding: 8px 12px;
}

/* split page: main column (left, 75%: request info, SOS bus, map, then everything else)
   | communication log (narrow right column, 25%) */
.sd-split {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.sd-pane-map { flex: 1 1 70%; min-width: 0; order: 1; }   /* MAIN column, on the left */
.sd-pane-log { flex: 1 1 30%; min-width: 0; order: 2; }   /* communication log, right */
.sd-pane-log .sd-comlog { margin-top: 0; }
/* the main column stacks: request info, SOS bus, map, then the vehicle/yard lists + itinerary */
.sd-pane-map .sd-body {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}
.sd-pane-map .sd-details { order: -4; width: 100%; }
.sd-pane-map .sd-group--sosbus { order: -3; width: 100%; margin-bottom: 0; }
.sd-itin--sosbus { margin: 6px 0 10px; }
.sd-pane-map .sd-mapwrap { order: -2; width: 100%; }
.sd-pane-map .sd-side { order: -1; }
.sd-pane-map .sd-itin { order: 0; width: 100%; }
.sd-pane-map .sd-map { height: 30vh; min-height: 200px; }
.sd-pane-map .sd-side {
  flex: 1 1 auto;
  width: 100%;
  max-height: 72vh;
  min-height: 0;
}
.sd-body {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.sd-side {
  flex: 0 0 240px;
  /* scroll pane the height of the map, so a long yard list doesn't stretch the page */
  max-height: 76vh;
  min-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sd-group {
  background: var(--fi-surface-sunken);
  border: 1px solid var(--fi-line);
  border-radius: 8px;
  padding: 8px 10px;
}
.sd-group-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fi-text-faint);
  margin-bottom: 4px;
}
/* the matching-location group headers are collapse toggles: reset the button chrome and
   keep the title look, with a hover cue and a caret that rotates open/closed */
.sd-group-title--toggle {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.sd-group-title--toggle:hover { color: var(--fi-link); }
.sd-group-title--toggle .fa { font-size: 11px; width: 10px; flex: none; }
/* count sits at the right edge of the group header */
.sd-group-count { margin-left: auto; padding-left: 8px; font-variant-numeric: tabular-nums; }
.sd-loc {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.sd-loc:hover {
  filter: brightness(0.97);
}
.sd-loc--selected {
  outline: 2px solid var(--fi-rail-accent);
  outline-offset: -2px;
}
.sd-loc-wrap {
  margin-bottom: 6px;
}
.sd-loc-wrap .sd-loc {
  margin-bottom: 0;
}
.sd-loc-contact {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--fi-text-muted);
}
.sd-contact-line {
  margin-bottom: 4px;
}
.sd-contact-phone {
  display: inline-block;
  font-weight: 600;
  color: var(--fi-link);
  text-decoration: none;
}
.sd-contact-phone:hover {
  text-decoration: underline;
}
.sd-contact-none {
  color: var(--fi-text-faint);
}
.sd-loc--source { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-loc--route { background: color-mix(in srgb, #2a8a7a 12%, var(--fi-surface)); color: color-mix(in srgb, #2a8a7a 55%, var(--fi-text)); }
.sd-loc--internal { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-loc--yard { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-loc--public { background: var(--fi-surface-sunken); color: var(--fi-text-muted); }
/* directory yard row: the clickable yard details on the left, the phone number always visible on the right */
.sd-loc--phone-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
}
.sd-loc--phone-row .sd-loc-main {
  flex: 1 1 auto;
  min-width: 0;
  padding: 7px 10px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.sd-loc-phone {
  flex: none;
  padding: 7px 10px 7px 0;
  font-weight: 600;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--fi-link);
  text-decoration: none;
  white-space: nowrap;
}
.sd-loc-phone:hover { text-decoration: underline; }
.sd-loc-phone--none { color: var(--fi-text-faint); font-weight: 500; }
/* compact single-line vehicle row: badge + ID + distance on one line, tighter padding */
.sd-loc--compact {
  display: flex;
  align-items: baseline;
  padding: 5px 10px;
}
.sd-veh-id {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}
.sd-veh-dist {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  opacity: 0.8;
}
/* straight-line distance from the SOS, shown next to a yard name in the yard lists */
.sd-loc-dist {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}
/* affordance pushed to the right edge of the compact vehicle row */
.sd-veh-hint {
  margin-left: auto;
  padding-left: 8px;
  flex: none;
  font-weight: 600;
  font-size: 11px;
  color: var(--fi-text-faint);
  white-space: nowrap;
}
.sd-loc-num {
  float: left;
  margin-right: 8px;
  min-width: 24px;
  padding: 1px 5px;
  border-radius: 6px;
  background: color-mix(in srgb, currentColor 15%, transparent);
  color: inherit;
  font-size: 11px;
  font-weight: 800;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.sd-loc-name { display: block; }
.sd-loc-hint {
  display: block;
  font-weight: 600;
  font-size: 11px;
  color: var(--fi-text-faint);
  margin-top: 2px;
}
.sd-loc--internal.sd-loc--selected { outline-color: var(--fi-success); }
.sd-loc-company {
  display: block;
  font-weight: 600;
  font-size: 12px;
  color: var(--fi-text-muted);
  margin-top: 1px;
}
.sd-loc-addr {
  display: block;
  font-weight: 400;
  font-size: 12px;
  color: var(--fi-text-faint);
  margin-top: 2px;
}
.sd-mapwrap {
  flex: 1 1 auto;
  min-width: 0;
}
.sd-map {
  width: 100%;
  height: 38vh;
  min-height: 240px;
  border-radius: 10px;
  overflow: hidden;
}
.sd-area-edit {
  margin-top: 8px;
  background: var(--fi-surface-sunken);
  border: 1px solid var(--fi-line);
  border-radius: 8px;
  padding: 8px 10px;
}
.sd-area-label { font-size: 12.5px; font-weight: 700; color: var(--fi-text-muted); }
.sd-area-hint { font-weight: 400; color: var(--fi-text-faint); }
.sd-area-slider { width: 100%; margin: 6px 0 4px; }
.sd-area-audience { font-size: 12.5px; color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sd-area-none { color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); font-weight: 400; }
.sd-area-list {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 22vh;
  overflow-y: auto;
}
.sd-area-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12.5px;
  padding: 3px 0;
  border-top: 1px solid var(--fi-line);
}
.sd-area-wrap { display: flex; flex-direction: column; }
/* clickable partner row: centers its yard on the map and reveals its address/contact */
.sd-area-row--btn {
  width: 100%;
  border: none;
  border-top: 1px solid var(--fi-line);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.sd-area-row--btn:hover { background: var(--fi-surface-sunken); }
.sd-area-row--sel { background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); }
.sd-area-num {
  flex: 0 0 auto;
  align-self: center;
  min-width: 22px;
  padding: 1px 5px;
  border-radius: 5px;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text));
  font-weight: 800;
  font-size: 11px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.sd-area-name { color: var(--fi-text); min-width: 0; flex: 1 1 auto; }
.sd-area-co { color: var(--fi-text-faint); }
.sd-area-dist { color: var(--fi-text-faint); font-variant-numeric: tabular-nums; white-space: nowrap; }
.sd-area-sub {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-area-sub--toggle {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.sd-area-sub--toggle:hover { color: var(--fi-link); }
.sd-area-sub--toggle .fa { font-size: 10px; width: 9px; }
.sd-area-veh {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  color: var(--fi-text-muted);
}
.sd-area-veh-note { color: var(--fi-text-faint); }

.sd-comlog {
  margin-top: 22px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 12px;
  padding: 16px 18px 18px;
}
.sd-comlog-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fi-text-muted);
  margin-bottom: 12px;
}
.sd-com {
  border-left: 3px solid var(--fi-line-strong);
  border-radius: 0 8px 8px 0;
  background: var(--fi-surface-sunken);
  padding: 8px 12px 10px 14px;
  margin-bottom: 12px;
}
.sd-com:last-child { margin-bottom: 0; }
.sd-com--received { border-left-color: var(--fi-danger); background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); }
.sd-com--dispatch { border-left-color: #23608f; background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); }
.sd-com--response { border-left-color: var(--fi-success); background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); }
.sd-com--broadcast { border-left-color: var(--fi-warning-accent); background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); }
.sd-com-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.sd-com-badge { font-size: 13px; font-weight: 700; color: var(--fi-text); }
.sd-com--reply { border-left-color: #6a4fb3; background: color-mix(in srgb, #6a4fb3 12%, var(--fi-surface)); }
.sd-com--comment { border-left-color: color-mix(in srgb, var(--fi-neutral) 45%, var(--fi-line)); background: var(--fi-surface-sunken); }
.sd-com--received .sd-com-badge { color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-com--dispatch .sd-com-badge { color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-com--response .sd-com-badge { color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-com--broadcast .sd-com-badge { color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-com--reply .sd-com-badge { color: color-mix(in srgb, #5a44a0 55%, var(--fi-text)); }
.sd-com--comment .sd-com-badge { color: var(--fi-text-muted); }
.sd-com-time { font-size: 13.5px; color: var(--fi-text-faint); font-variant-numeric: tabular-nums; }
.sd-com-who { font-size: 14px; color: var(--fi-text-faint); margin-top: 2px; text-align: left; }
.sd-com-sublabel { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--fi-text-faint); margin: 8px 0 2px; }
.sd-com-fields {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 10px;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--fi-warning-accent) 35%, var(--fi-line));
  border-radius: 8px;
}
.sd-com-field { display: flex; gap: 8px; align-items: baseline; font-size: 12.5px; }
.sd-com-field-k {
  flex: 0 0 96px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-com-field-v { color: var(--fi-text); min-width: 0; word-break: break-word; }
.sd-com-body {
  margin: 6px 0 0;
  padding: 8px 10px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 8px;
  font-family: inherit;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--fi-text);
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: none;   /* show the full note / message, never clip it for space */
  overflow-y: visible;
}
.sd-com-link { color: var(--fi-link); word-break: break-all; }
.sd-com-open {
  display: inline-block;
  margin-left: 6px;
  color: var(--fi-link);
  text-decoration: none;
}
.sd-com-open:hover { color: var(--fi-link); }
.sd-com-warn {
  margin-top: 8px;
  font-size: 12.5px;
  color: var(--fi-danger);
}
.sd-topbar {
  margin-bottom: 12px;
}
.sd-toprow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
}
.sd-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--fi-text);
  line-height: 1.25;
  min-width: 0;
  word-break: break-word;
}
.sd-topactions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.sd-topbar .sd-bcast-edit { margin-top: 10px; }
/* rebroadcast panel now sits below the communication log in the right column */
.sd-rebcast--panel {
  margin-top: 16px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 12px;
  padding: 14px 16px;
}
.sd-com-add {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--fi-line);
}
.sd-com-add-send { align-self: flex-end; }

@media (max-width: 768px) {
  .sd {
    padding: 8px;
  }
  .sd-topbar { margin-bottom: 8px; }
  .sd-details {
    gap: 2px 12px;
    padding: 7px 10px;
    margin-bottom: 8px;
  }
  .sd-detail-label { font-size: 11px; }
  .sd-detail-value {
    font-size: 14px;
  }
  .sd-detail-value,
  .sd-detail--wide .sd-detail-value {
    max-width: 68vw;
  }
  /* shrink the page-rail header ("SOS Request Sender Details") on mobile */
  .sd >>> .fi-pr-title {
    font-size: 16px;
  }
  .sd-title {
    font-size: 16px;
  }
  /* drop "request for <vehicle>" from the header to save width; it moves into the details below */
  .sd-title-subject {
    display: none;
  }
  .sd-detail--mobile-only {
    display: flex;
  }
  /* icon-only lifecycle buttons on mobile to save width */
  .sd-topactions .sd-btn-label {
    display: none;
  }
  /* the split stacks with the communication log on top, then map + yards
     (the panes keep their desktop order: log = 1, map = 2) */
  .sd-split {
    flex-direction: column;
    gap: 12px;
  }
  .sd-pane-map,
  .sd-pane-log {
    flex: 1 1 auto;
    width: 100%;
  }
  .sd-body {
    flex-direction: column;
    align-items: stretch;   /* fill width in column mode (flex-start left the map sized to content) */
    gap: 10px;
  }
  .sd-mapwrap {
    order: -1;   /* map first on mobile */
    width: 100%;
    max-width: 100%;
    overflow: hidden;   /* clip any map-tile / control bleed that would side-scroll the page */
  }
  .sd-side {
    flex: 1 1 auto;
    max-height: 240px;
    width: 100%;
  }
  .sd-map {
    width: 100%;
    max-width: 100%;
    height: 32vh;
    min-height: 190px;
  }
  .sd-com-body {
    max-height: none;
  }
}
</style>
